import React from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { classNames } from '../../utils';
import { ChildrenProps } from '@cotera/client/app/components/utils';
import { makeStore, StateSetter } from '@cotera/client/app/etc';
import { Text } from '../../ui/text';

type State = {
  open: boolean;
};
const actions = (set: StateSetter<State>) => ({
  toggle: (open?: boolean) => set((state) => ({ open: open ?? !state.open })),
});
type Actions = ReturnType<typeof actions>;

export const { provider: DropdownProvider, hook: useDropdown } = makeStore<
  State,
  Actions
>();

export const Dropdown: React.FC<{
  children: React.ReactNode;
  open?: boolean;
}> = ({ children, open }) => (
  <DropdownProvider
    state={{
      open: open ?? false,
    }}
    actions={actions}
  >
    <View>{children}</View>
  </DropdownProvider>
);

const View: React.FC<ChildrenProps> = ({ children }) => {
  const open = useDropdown((s) => s.open);
  const toggle = useDropdown((s) => s.actions.toggle);

  return (
    <DropdownMenu.Root onOpenChange={toggle} open={open}>
      {children}
    </DropdownMenu.Root>
  );
};

export const DropdownContent: React.FC<
  ChildrenProps & {
    align?: 'start' | 'end' | 'center';
    side?: 'bottom' | 'top' | 'right' | 'left';
    style?: React.CSSProperties;
    priority?: string;
    avoidCollisions?: boolean;
    collisionPadding?: number;
    sticky?: 'always' | 'partial' | undefined;
  }
> = ({
  children,
  align = 'center',
  side = 'bottom',
  style,
  priority = 'z-10',
  avoidCollisions = false,
  collisionPadding = 0,
  sticky = 'always',
}) => {
  return (
    <DropdownMenu.Portal>
      <DropdownMenu.Content
        align={align}
        side={side}
        sideOffset={5}
        className={classNames(
          'flex flex-col justify-center items-center',
          priority
        )}
        style={style}
        avoidCollisions={avoidCollisions}
        collisionPadding={collisionPadding}
        sticky={sticky}
      >
        <div className="bg-white rounded px-2 py-3 shadow-lg border-divider border min-w-[150px] space-y-2 text-gray-800">
          {children}
        </div>
        <DropdownMenu.Arrow className="fill-gray-300" />
      </DropdownMenu.Content>
    </DropdownMenu.Portal>
  );
};

export const DropdownItem = ({
  children,
  icon = null,
  active,
  onClick,
  disabled,
  closeOnClick,
  className,
  hoverStyle = true,
}: {
  icon?: JSX.Element | null;
  active?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  closeOnClick?: boolean;
  className?: string;
  hoverStyle?: boolean;
} & ChildrenProps) => {
  const toggle = useDropdown((s) => s.actions.toggle);

  return (
    <div
      className={classNames(
        'flex flex-row items-center px-2 py-2 ring-0 hover:ring-0 hover:outline-hidden rounded w-full',
        className,
        active ? 'cursor-default' : hoverStyle ? 'hover:bg-gray-100' : '',
        disabled ? 'cursor-default text-muted-text' : 'cursor-pointer'
      )}
      role="button"
      onClick={
        disabled
          ? undefined
          : () => {
              onClick?.();
              if (closeOnClick) {
                toggle();
              }
            }
      }
    >
      {icon}
      <Text.Span
        className={classNames(
          'ml-2 w-full',
          active ? 'text-chart-1' : disabled ? 'text-destructive' : ''
        )}
      >
        {children}
      </Text.Span>
    </div>
  );
};

export const DropdownTrigger: React.FC<ChildrenProps> = ({ children }) => {
  return <DropdownMenu.Trigger asChild>{children}</DropdownMenu.Trigger>;
};
